import { useCallback, useMemo, useState } from "react";

import { NextRouter, useRouter } from "next/router";
import { Button, LoadingSpinner, ChevronUpSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import useSingleContent from "@/content/useSingleContentQuery/useSingleContentQuery";
import GeneaologyPanel from "./components/GeneaologyPanel";
import AttachmentPanel from "./components/AttachmentPanel";
import useLangstonAttachmentsQuery from "./hooks/useLangstonAttachmentsQuery";
import {
  mainContainerCss,
  centerContentPaneCss,
  resourceRegionCss,
  resourceContainerCss,
  viewResourcesIconCss,
  resourceTabButtonCss,
  resourceTabInnerContentCss,
  outlineToggleButtonCss,
  outlineToggleLabelCss,
  chevronIconCss,
  outlineContainerCss,
  outlineWrapperCss,
} from "./BundlePage.styles";
import { TreeItem } from "./types";
import MainContent from "./components/MainContent/MainContent";

import { LessonSparkAttachment } from "./types/LessonSparkAttachment";

const ButtonWithTrackEvent = withTrackEvent(Button);

const CURRENT_CONTENT_ID_PARAM = "tree";

export type ContentOutline = {
  initial_focused_item_id: string;
  items: Record<string, TreeItem>;
  root_item_id: string;
};

const updateCurrentContentIdInUrl = (router: NextRouter, nodeId: string) => {
  const currentNodeId = router.query[CURRENT_CONTENT_ID_PARAM];
  if (currentNodeId === nodeId) return;
  router.replace(
    {
      pathname: router.pathname,
      query: { ...router.query, [CURRENT_CONTENT_ID_PARAM]: nodeId },
    },
    undefined,
    { shallow: true },
  );
};

const BundlePage = () => {
  const router = useRouter();
  const { query } = router;
  const { contentId, tree } = query as {
    contentId: string;
    tree?: string;
  };
  const currentContentIdTreeRouteOrDefault = tree ?? contentId;

  const [isResourceOpen, setIsResourcePanelOpen] = useState(false);
  const [isOutlineOpen, setIsOutlineOpen] = useState(false);
  const { data: contentOutline, isLoading: outlineIsLoading } =
    useSingleContent<ContentOutline>({
      contentId,
      queryParams: {
        viewmodel_type: "headless_tree",
      },
    });
  const treeRootTitle = useMemo(() => {
    const treeItems = contentOutline?.items ?? {};
    const rootItem = treeItems[contentOutline?.root_item_id as string];

    if (!rootItem) {
      return "Outline";
    } else {
      return rootItem?.name || "";
    }
  }, [contentOutline]);

  const onSelectOutlineItem = useCallback(
    (id: string) => updateCurrentContentIdInUrl(router, id),
    [router],
  );
  // With the headless tree data structure in mind, `currentContentId` is the
  // content ID of a child content defined by `tree`. If we don't have the tree
  // of a selected child content, `currentContentId` defaults to `contentId`.
  const currentContentId =
    contentOutline?.items?.[currentContentIdTreeRouteOrDefault]?.object_id ??
    contentId;
  const { data: attachmentData, isFetching: isAttachmentsFetching } =
    useLangstonAttachmentsQuery(currentContentId);

  const isResourcePanelHidden =
    isAttachmentsFetching ||
    (!isAttachmentsFetching && !attachmentData) ||
    (!isAttachmentsFetching &&
      !(attachmentData as LessonSparkAttachment[]).length);

  if (outlineIsLoading) {
    return <LoadingSpinner />;
  }

  if (!contentOutline) {
    return <div>This content is not available or doesn't exist.</div>;
  }

  return (
    <div css={mainContainerCss(isResourcePanelHidden)}>
      <section>
        {/* Small screens: sticky toggle button */}
        <ButtonWithTrackEvent
          __cssFor={{ root: outlineToggleButtonCss }}
          flavor={Button.flavors.incognito}
          onClick={() => setIsOutlineOpen((isOpen) => !isOpen)}
          actionName={actionNames.interact}
          objectType={objectTypes.content}
          streamProperties={{
            description:
              "Open bundle page outline tree on tablet/mobile screens",
          }}
        >
          <span css={outlineToggleLabelCss}>{treeRootTitle}</span>
          <ChevronUpSVG css={chevronIconCss(isOutlineOpen)} />
        </ButtonWithTrackEvent>

        {/* Collapsible container on < md, static column on ≥ md */}
        <div css={outlineWrapperCss}>
          <div css={outlineContainerCss(isOutlineOpen)}>
            <GeneaologyPanel
              contentOutline={contentOutline}
              updateContentId={onSelectOutlineItem}
              currentContentIdOrTree={currentContentIdTreeRouteOrDefault}
            />
          </div>
        </div>
      </section>

      <div css={centerContentPaneCss}>
        <MainContent contentId={currentContentId} />
      </div>

      {isResourcePanelHidden ? null : (
        <div css={resourceRegionCss}>
          {/* Bottom sticky tab (md and down) */}
          <ButtonWithTrackEvent
            __cssFor={{ root: resourceTabButtonCss }}
            onClick={() => setIsResourcePanelOpen(true)}
            aria-expanded={isResourceOpen}
            flavor={Button.flavors.incognito}
            actionName={actionNames.interact}
            objectType={objectTypes.content}
            streamProperties={{
              description:
                "Open bundle page resource panel on tablet/mobile screens",
            }}
          >
            <div css={resourceTabInnerContentCss}>
              <span>View Resources</span>
              <ChevronUpSVG css={viewResourcesIconCss} />
            </div>
          </ButtonWithTrackEvent>

          {/* Drawer on < md, right-hand sidebar on ≥ md */}
          <section css={resourceContainerCss(isResourceOpen)}>
            <AttachmentPanel
              key={currentContentId}
              attachmentData={attachmentData as LessonSparkAttachment[]}
              onClose={() => setIsResourcePanelOpen(false)}
            />
          </section>
        </div>
      )}
    </div>
  );
};

export default BundlePage;
